import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Screen } from '../components/AppShell';
import { RegPlate, StatusText, StatusPill, Skeleton, EmptyState, SectionHeader } from '../components/ui';
import { api } from '../lib/api';
import { useLoad } from '../lib/hooks';
import {
  addWeeks,
  formatDate,
  formatIsoWeek,
  isoWeek,
  reminderStatus,
  today,
  forwardPlan,
} from '../../../shared/schedule';
import './screens.css';

interface PlannerAsset {
  id: string;
  regNo: string | null;
  fleetNo: string | null;
  kind: string;
  make: string | null;
  model: string | null;
  customerName: string | null;
  intervalWeeks: number;
  lastInspectionDate: string | null;
  nextInspectionDate: string | null;
}

const HORIZON_WEEKS = 13;

/**
 * The forward inspection schedule DVSA expect an operator to hold. Each row is
 * a vehicle; each column a week. A filled cell is a planned inspection.
 */
export default function Planner() {
  const { data, loading, error, reload } = useLoad<{ assets: PlannerAsset[] }>(() => api.get('/planner'));
  const [kind, setKind] = useState<'all' | 'vehicle' | 'trailer'>('all');

  const weeks = useMemo(() => {
    const start = today();
    return Array.from({ length: HORIZON_WEEKS }, (_, i) => {
      const date = addWeeks(start, i);
      return { date, ...isoWeek(date) };
    });
  }, []);

  const assets = useMemo(() => {
    const list = data?.assets ?? [];
    return kind === 'all' ? list : list.filter((a) => a.kind === kind);
  }, [data, kind]);

  /**
   * Weeks in the horizon this vehicle is scheduled for.
   *
   * A vehicle whose due date has already passed would otherwise have an empty
   * row, because its week is behind the start of the grid. An overdue
   * inspection is the most important thing on this screen, so it is pulled
   * forward onto the current week and flagged.
   */
  const scheduleFor = (asset: PlannerAsset) => {
    const dates = [
      asset.nextInspectionDate,
      ...forwardPlan(asset.nextInspectionDate ?? asset.lastInspectionDate, asset.intervalWeeks, 8),
    ].filter(Boolean) as string[];

    const planned = new Set(dates.map((d) => formatIsoWeek(d)));
    const overdue = Boolean(asset.nextInspectionDate && asset.nextInspectionDate < today());
    if (overdue) planned.add(formatIsoWeek(today()));
    return { planned, overdue };
  };

  return (
    <Screen
      onRefresh={reload} title="Inspection planner" subtitle={`Next ${HORIZON_WEEKS} weeks`} back="/" wide>
      <div className="stack-4">
        <div className="chips">
          {(['all', 'vehicle', 'trailer'] as const).map((k) => (
            <button
              key={k}
              type="button"
              className={`chip${kind === k ? ' is-on' : ''}`}
              onClick={() => setKind(k)}
            >
              {k === 'all' ? 'All' : k === 'vehicle' ? 'Vehicles' : 'Trailers'}
            </button>
          ))}
        </div>

        {error ? <div className="notice notice-danger">{error}</div> : null}
        {loading && !data ? <Skeleton rows={5} /> : null}

        {data && assets.length === 0 ? (
          <EmptyState title="Nothing to plan" body="Add vehicles with an inspection interval to build a schedule." />
        ) : null}

        {assets.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="data planner">
                <thead>
                  <tr>
                    <th className="planner-sticky">Vehicle</th>
                    <th>Every</th>
                    {weeks.map((w) => (
                      <th key={w.date} className="planner-week" title={formatDate(w.date)}>
                        {w.week}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => {
                    const { planned: scheduled, overdue } = scheduleFor(asset);
                    const status = reminderStatus(asset.nextInspectionDate, 'inspection');
                    return (
                      <tr key={asset.id}>
                        <td className="planner-sticky">
                          <Link to={`/fleet/${asset.id}`} className="row row-tight">
                            <RegPlate reg={asset.regNo} fleetNo={asset.fleetNo} size="sm" />
                          </Link>
                        </td>
                        <td className="num">{asset.intervalWeeks}w</td>
                        {weeks.map((w, index) => {
                          const key = `${w.year}-W${String(w.week).padStart(2, '0')}`;
                          const isPlanned = scheduled.has(key);
                          const isOverdueNow = overdue && index === 0;
                          return (
                            <td key={w.date} className="planner-cell">
                              {isPlanned ? (
                                <span
                                  className={`planner-mark${isOverdueNow ? ' is-overdue' : ''}`}
                                  title={
                                    isOverdueNow
                                      ? `${asset.regNo ?? asset.fleetNo} — ${status.label}`
                                      : `${asset.regNo ?? asset.fleetNo} — week ${w.week}`
                                  }
                                />
                              ) : null}
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <p className="hint">Column headings are ISO week numbers. Scroll sideways for later weeks.</p>

            <section className="stack">
              <SectionHeader title="Next due" />
              <div className="panel divide">
                {[...assets]
                  .sort((a, b) => (a.nextInspectionDate ?? 'z').localeCompare(b.nextInspectionDate ?? 'z'))
                  .map((asset) => {
                    const status = reminderStatus(asset.nextInspectionDate, 'inspection');
                    return (
                      <Link key={asset.id} to={`/fleet/${asset.id}`} className="listrow">
                        <div className="listrow-lead">
                          <RegPlate reg={asset.regNo} fleetNo={asset.fleetNo} size="sm" />
                        </div>
                        <div className="grow">
                          <div className="listrow-title truncate">
                            {[asset.make, asset.model].filter(Boolean).join(' ') || 'Vehicle'}
                          </div>
                          <div className="listrow-sub truncate">
                            {asset.customerName ?? 'No customer'}
                            <span className="dot" />
                            {asset.nextInspectionDate ? formatDate(asset.nextInspectionDate) : 'Not scheduled'}
                          </div>
                        </div>
                        {status.state === 'overdue' ? (
                          <StatusPill state="overdue" size="sm">
                            {status.label}
                          </StatusPill>
                        ) : (
                          <StatusText state={status.state}>{status.label}</StatusText>
                        )}
                      </Link>
                    );
                  })}
              </div>
            </section>
          </>
        )}
      </div>
    </Screen>
  );
}
